import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Activity as ActivityIcon } from "lucide-react";

const ACTION_META = {
  started: { l: "Started", color: "orange" },
  completed: { l: "Ready for approval", color: "blue" },
  approved: { l: "Approved", color: "green" },
  posted: { l: "Published", color: "green" },
  story: { l: "Stories", color: "blue" },
  task_completed: { l: "Task completed", color: "green" },
  day_confirmed: { l: "Day confirmed", color: "grey" },
};

export default function ActivityLog() {
  const { users } = useAuth();
  const [userF, setUserF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const params = { limit: 200 };
  if (userF !== "all") params.user_id = userF;
  if (typeF !== "all") params.action_type = typeF;
  const { data = [], isLoading } = useQuery({ queryKey: ["activity-feed", userF, typeF], queryFn: async () => (await api.get("/activity", { params })).data });

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Activity Log</h1>
        <p className="mt-1 text-sm text-zinc-500">Every meaningful action across the agency, newest first.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={userF} onValueChange={setUserF}>
          <SelectTrigger className="w-52" data-testid="activity-user-filter"><SelectValue placeholder="Employee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Employees</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeF} onValueChange={setTypeF}>
          <SelectTrigger className="w-52" data-testid="activity-type-filter"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {Object.entries(ACTION_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-lg border-zinc-200 p-5 shadow-sm">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
        ) : data.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-400">No activity yet</div>
        ) : (
          <div className="space-y-3">
            {data.map((a) => {
              const m = ACTION_META[a.action_type];
              return (
                <div key={a.id} className="flex items-start gap-3 border-b border-zinc-50 pb-3 last:border-0" data-testid={`activity-row-${a.id}`}>
                  <ActivityIcon className="mt-0.5 h-4 w-4 shrink-0 text-zinc-300" />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-zinc-900">{a.user_name}</span>
                      {m && <Badge color={m.color}>{m.l}</Badge>}
                    </div>
                    <div className="text-sm text-zinc-600">{a.action}</div>
                    <div className="text-xs text-zinc-400">{new Date(a.created_at).toLocaleString("en-IN")}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
